import type { CCOType } from "~/lib/schemas/db-enums";

import { CCO_TYPE_ORDER } from "./cco-type-order";

/**
 * The shape `countGroupedCCOs` actually reads: a two-level
 * `type → subcategory → items` map where only the leaf array lengths matter.
 *
 * Deliberately narrower than `~/lib/ccos`'s `GroupedCCOsByType` (which it
 * structurally accepts) so callers and tests need no cast to satisfy the full
 * `CCO` record.
 */
type CountableGroupedCCOs = {
  readonly [type in CCOType]?: Readonly<Record<string, readonly unknown[]>>;
};

/**
 * Count the organizations a `/organizations` visitor would actually see.
 *
 * The input is a two-level map (`type → subcategory → CCO[]`), so BOTH levels
 * must be flattened to reach the leaf CCOs. Only types in `CCO_TYPE_ORDER` are
 * counted — that is the same list `/organizations` renders, which keeps this
 * count and that page in agreement by construction.
 *
 * Regression guard: the hub page once used `Object.values(grouped).flat()`,
 * which unwrapped only the first level and left the two per-type buckets as
 * (non-array) objects — `.flat()` is a no-op on those, so `.length` returned
 * the number of CCO *types* (2), not organizations.
 *
 * Aliases: organization count, CCO total, hub tile count.
 */
export function countGroupedCCOs(grouped: CountableGroupedCCOs): number {
  return CCO_TYPE_ORDER.flatMap((type) =>
    Object.values(grouped[type] ?? {}),
  ).reduce((total, ccos) => total + ccos.length, 0);
}
